// ObjectDataViewer — per-Object-Type drawer that browses the materialized
// object instances (rows in the `objects` table) for a single Object Type.
//
// Opened from the ObjectTypeList "View data" affordance. Columns are derived
// from the Object Type's property definitions; values come from each instance's
// jsonb `data` field, keyed by property api_name.

import { useMemo, useState } from "react";
import { Database } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useObjects } from "@/hooks/useObjects";
import { useObjectType } from "@/hooks/useObjectTypes";
import { formatCurrency } from "@/lib/currency";
import type { PropertyKind } from "@/types/ontology";

const RECORD_LIMIT = 50;

/** Format a raw jsonb value for display based on the property kind. */
export function formatPropertyValue(value: unknown, kind: PropertyKind): string {
  if (value === null || value === undefined || value === "") return "—";

  switch (kind) {
    case "currency": {
      const n = typeof value === "number" ? value : Number(value);
      return Number.isFinite(n) ? formatCurrency(n) : String(value);
    }
    case "number": {
      const n = typeof value === "number" ? value : Number(value);
      return Number.isFinite(n) ? n.toLocaleString() : String(value);
    }
    case "bool": {
      if (typeof value === "boolean") return value ? "Yes" : "No";
      const s = String(value).toLowerCase();
      if (s === "true" || s === "t" || s === "1") return "Yes";
      if (s === "false" || s === "f" || s === "0") return "No";
      return String(value);
    }
    case "date": {
      const d = new Date(String(value));
      return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
    }
    default:
      return typeof value === "object" ? JSON.stringify(value) : String(value);
  }
}

interface ObjectDataViewerProps {
  /** Object Type id to view; null closes the drawer. */
  objectTypeId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ObjectDataViewer({ objectTypeId, open, onOpenChange }: ObjectDataViewerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* Mount the data-loading content only while open so the underlying
          TanStack Query hooks aren't invoked (and don't require a
          QueryClient) when the drawer is closed. */}
      {open && objectTypeId !== null && <ObjectDataViewerContent objectTypeId={objectTypeId} />}
    </Sheet>
  );
}

function ObjectDataViewerContent({ objectTypeId }: { objectTypeId: string }) {
  const [search, setSearch] = useState("");

  const { objectType, isLoading: isTypeLoading } = useObjectType(objectTypeId);
  const { objects, isLoading: isDataLoading } = useObjects({
    objectTypeId,
    search,
    limit: RECORD_LIMIT,
  });

  // Stable column list derived from the Object Type's property definitions.
  const columns = useMemo(
    () =>
      (objectType?.property_types ?? [])
        .slice()
        .sort((a, b) => a.position - b.position)
        .map((p) => ({
          apiName: p.api_name,
          header: p.name || p.api_name,
          kind: p.kind,
        })),
    [objectType]
  );

  const isLoading = isTypeLoading || isDataLoading;
  const truncated = objects.length >= RECORD_LIMIT;

  return (
    <SheetContent side="right" className="w-full sm:max-w-3xl max-h-[85vh] flex flex-col p-0">
      <SheetHeader className="border-b px-5 py-4 shrink-0 space-y-1">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-muted-foreground" />
          <SheetTitle className="text-base font-semibold">
            {objectType?.name ?? "Object data"}
          </SheetTitle>
        </div>
        <SheetDescription>
          {isLoading
            ? "Loading records…"
            : `${objects.length} record${objects.length === 1 ? "" : "s"}${
                truncated ? ` (showing first ${RECORD_LIMIT})` : ""
              }`}
        </SheetDescription>
      </SheetHeader>

      <div className="px-5 py-3 border-b shrink-0">
        <Input
          type="search"
          placeholder="Search records…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search records"
        />
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        {isLoading ? (
          <div className="p-5 space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-9 rounded-md" />
            ))}
          </div>
        ) : objects.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3 text-center px-6">
            <Database className="w-10 h-10 text-muted-foreground opacity-40" />
            <p className="text-sm text-muted-foreground">
              {search.trim()
                ? "No records match your search."
                : "No data has been materialized for this Object Type yet."}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader className="sticky top-0 z-10">
              <TableRow>
                {columns.map((col) => (
                  <TableHead key={col.apiName} className="whitespace-nowrap">
                    {col.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {objects.map((obj) => (
                <TableRow key={obj.id}>
                  {columns.map((col) => (
                    <TableCell key={col.apiName} className="whitespace-nowrap">
                      {formatPropertyValue(obj.data?.[col.apiName], col.kind)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </SheetContent>
  );
}
